import React, { useState, useRef } from "react";
import { ArrowBigRightDash, Paperclip } from "lucide-react";
import { ClipLoader } from "react-spinners";
import { useChatContext } from "../../Context/ChatContext";
import { useTheme } from "../../Context/ThemeContext";
import { queryApi } from "../../Api/BaseApi";
import "./index.css";

// Accepted portfolio formats — kept in sync with the file input's `accept`
// attribute and the backend's file_ingest.SUPPORTED_EXTENSIONS.
const SUPPORTED_EXTENSIONS = ['.xlsx', '.xls', '.txt', '.md'];
const UNSUPPORTED_FILE_MESSAGE =
    'Unsupported file type. Please upload an Excel (.xlsx/.xls), plain text (.txt), or Markdown (.md) file.';

const ChatInput = () => {
    const [inputValue, setInputValue] = useState("");
    const [autoSend, setAutoSend] = useState(false);
    const fileInputRef = useRef(null);
    const { addConversationTurn, addUploadTurn, addAssistantNotice, sessionPortfolio, isLoading: isChatBusy } = useChatContext();
    const { isDarkMode } = useTheme();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file
        if (!file || isChatBusy) return;
        // Client-side guard: reject unsupported formats with a clear inline chat
        // error before uploading. (The backend enforces the same check.)
        const name = (file.name || '').toLowerCase();
        if (!SUPPORTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
            addAssistantNotice(`📎 ${file.name}`, UNSUPPORTED_FILE_MESSAGE);
            return;
        }
        await addUploadTurn(file, autoSend);
    };

    // const COMMANDS = {
    //     "/customer": "/api/analysis/customer/",
    //     "/invoice": "/api/analysis/invoice/",
    //     "/fine-tune": "/api/model/fine-tune/",
    //     "/query": "/api/analysis/query/",
    // };

    // const filteredCommands = Object.keys(COMMANDS).filter((cmd) =>
    //     cmd.startsWith(inputValue.trim())
    // );

    const handleInputChange = (e) => {
        setInputValue(e.target.value);

        // let val = e.target.value;
        // setInputValue(val);
        // setShowDropdown(val.startsWith("/"));
        // // Reset selected index when input changes
        // setSelectedIndex(0);
    };

    // const handleKeyDown = (e) => {
    //     // Only handle keys if dropdown is shown
    //     if (!showDropdown || filteredCommands.length === 0) return;

    //     switch (e.key) {
    //         case 'ArrowDown':
    //             e.preventDefault(); // Prevent cursor from moving
    //             setSelectedIndex(prev => 
    //                 prev < filteredCommands.length - 1 ? prev + 1 : prev
    //             );
    //             break;

    //         case 'ArrowUp':
    //             e.preventDefault(); // Prevent cursor from moving
    //             setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
    //             break;

    //         case 'Enter':
    //             if (showDropdown && filteredCommands.length > 0) {
    //                 e.preventDefault(); // Prevent form submission
    //                 handleCommandClick(filteredCommands[selectedIndex]);
    //             }
    //             break;

    //         case 'Escape':
    //             setShowDropdown(false);
    //             break;

    //         default:
    //             break;
    //     }
    // };

    // Create a ref for the input element
    // const inputRef = React.useRef(null);

    // const handleCommandClick = (cmd) => {
    //     setInputValue(cmd + " ");
    //     setShowDropdown(prev=>!prev.showDropdown);

    //     // Focus the input element after selecting a command
    //     setTimeout(() => {
    //         if (inputRef.current) {
    //             inputRef.current.focus();
    //         }
    //     }, 10);
    // };


    // uncomment the following code to test functionality
    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     if (!inputValue.trim() || isLoading) return;

    //     const question = inputValue.trim();
    //     setInputValue("");
    //     setLoading(true);
    //     // setShowDropdown(false);

    //     // Simulate API call with timeout
    //     setTimeout(() => {
    //         // Mock response - in a real app, this would come from an API
    //         const answer = `This is a sample response to: "${question}"`;
    //         addResponse(question, answer);
    //         setLoading(false);
    //     }, 1000);
    // };

    // Uncomment the following code to enable actual API call
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isChatBusy) return;
        const question = inputValue.trim();
        setInputValue("");
        // addConversationTurn handles setLoading internally. Bind the current
        // Auto-send toggle state so a typed dispatch request / customer list is
        // processed and sent by the backend pipeline (mirrors the upload path).
        const sendOn = autoSend;
        await addConversationTurn(question, (q, cust) => queryApi(q, cust, sendOn));
    };


    return (
        <div className={`chat-input-container ${isDarkMode ? 'dark-mode' : ''}`}>
            {sessionPortfolio && sessionPortfolio.filename && (
                <div className={`portfolio-loaded-badge ${isDarkMode ? 'dark-mode' : ''}`}
                     title="Active portfolio context — the assistant is aware of this uploaded data">
                    <span className="portfolio-dot" />
                    <span className="portfolio-text">
                        Portfolio loaded: <strong>{sessionPortfolio.filename}</strong>
                        {` · ${sessionPortfolio.totalCustomers ?? (sessionPortfolio.customerIds || []).length} customers`}
                        {` · ${sessionPortfolio.totalInvoices ?? 0} invoices`}
                    </span>
                </div>
            )}
            <form onSubmit={handleSubmit} className={`input-form ${isDarkMode ? 'dark-mode' : ''}`}>

                {/* {showDropdown && filteredCommands.length > 0 && (
                    <ul className={`command-dropdown ${isDarkMode ? "dark-mode" : ""}`}>
                        {filteredCommands.map((cmd, index) => (
                            <li 
                                key={cmd} 
                                onClick={() => handleCommandClick(cmd)}
                                className={index === selectedIndex ? 'selected' : ''}
                            >
                                {cmd}
                            </li>
                        ))}
                    </ul>
                )} */}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.txt,.md"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                />

                {/* Primary row: the text field is full-width and unobstructed,
                    with the send button anchored at its right. */}
                <div className="input-row">
                    <input
                        // ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        // onKeyDown={handleKeyDown}
                        placeholder={isChatBusy ? "Waiting for response..." : (autoSend
                            ? "Auto-send ON — type or attach customer IDs (e.g. CUST00007, CUST00012)"
                            : "Ask a question, or attach a portfolio file (.xlsx, .txt, .md)...")}
                        disabled={isChatBusy}
                        className={`chat-input ${isDarkMode ? 'dark-mode' : ''}`}
                        autoComplete="off"
                    />
                    <button
                        type="submit"
                        disabled={isChatBusy || !inputValue.trim()}
                        className={`submit-button ${isDarkMode ? 'dark-mode' : ''}`}
                    >
                        {isChatBusy ? (
                            <ClipLoader color={isDarkMode ? '#ffffff' : '#000000'} size={16} />
                        ) : (
                            <ArrowBigRightDash strokeWidth={1.5} />
                        )}
                    </button>
                </div>

                {/* Secondary toolbar: quiet, left-aligned options beneath the input. */}
                <div className="input-toolbar">
                    <button
                        type="button"
                        title="Upload a portfolio (CSV, XLSX, MD, TXT)"
                        disabled={isChatBusy}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        className={`attach-button ${isDarkMode ? 'dark-mode' : ''}`}
                    >
                        <Paperclip strokeWidth={1.5} size={18} />
                    </button>
                    <div
                        className={`autosend-toggle-wrap ${isDarkMode ? 'dark-mode' : 'light-mode'} ${isChatBusy ? 'disabled' : ''}`}
                        title="When on, uploading a portfolio also sends the internal action notifications to your team automatically. Pinaka never contacts your customers."
                    >
                        <span className="toggle-shell">
                            <div className="toggle-cont">
                                <input
                                    className="toggle-input"
                                    id="autosend-toggle-input"
                                    type="checkbox"
                                    checked={autoSend}
                                    onChange={(e) => setAutoSend(e.target.checked)}
                                    disabled={isChatBusy}
                                />
                                <label className="toggle-label" htmlFor="autosend-toggle-input">
                                    <div className="cont-icon">
                                        <span style={{ "--width": 25, "--deg": 45, "--duration": 8 }} className="sparkle"></span>
                                        <span style={{ "--width": 25, "--deg": 120, "--duration": 13 }} className="sparkle"></span>
                                        <span style={{ "--width": 25, "--deg": 230, "--duration": 11 }} className="sparkle"></span>
                                        <span style={{ "--width": 25, "--deg": 300, "--duration": 16 }} className="sparkle"></span>
                                        <svg viewBox="0 0 24 24" className="icon" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11 21h-1l1-7H6.5c-.88 0-.33-.75-.31-.78C7.48 10.94 9.66 7.05 12.87 1.5h1l-1 7h3.51c.4 0 .62.19.4.66C12.95 13.06 11 21 11 21z"></path>
                                        </svg>
                                    </div>
                                </label>
                            </div>
                        </span>
                        <span className="autosend-label">Auto-send</span>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ChatInput;